import React, { useCallback, useMemo, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { InputLabel } from '@picsio/ui';

import { updateListing } from '../../../store/products/updateListing';
import { updateListingVariant } from '../../../store/products/updateListingVariant';
import { addListingKeywords } from '../../../store/products/addListingKeywords';
import { removeListingKeywords } from '../../../store/products/removeListingKeywords';
import { getActiveProduct } from '../../../store/products/selectors';
import {
  selectSelectedListingProduct,
  selectSelectedListingVariants,
  selectStagingDiff,
  selectVariantStagingDiff,
  selectProductIsBusy,
  selectEffectiveChannelState,
  selectMasterProduct,
} from '../../../store/selectors/productSelectors';
import { setCookie } from '../../../shared/utils';
import localization from '../../../shared/strings';
import { getDetailsPanelVisibility } from '../../details/helpers/getDetailsPanelDefaultState';
import RelativeTime from '../../RelativeTime';
import Description from '../Description';
import Inventory from '../Inventory';
import Options from '../Options';
import Pricing from '../Pricing';
import picsioConfig from '../../../../../../config';
import ProductStatusSelect from '../ProductStatusSelect';
import ProductType from '../ProductType';
import Category from '../Category';
import { navigateToVariantParent } from '../helpers/navigateToVariantParent';
import { getVariantUnitCost } from '../helpers/getVariantUnitCost';
import { INVENTORY_PRESETS, SHIPPING_PRESETS } from '../helpers/productDetailsPresets';
import Shipping from '../Shipping';
import VariantDetailsHeader from '../VariantDetailsHeader';
import VariantContextBlock from '../VariantContextBlock';
import DetailsBlock from '../../DetailsBlock';
import ProductName from '../ProductName';
import Brand from '../Vendor';
import Keywords from '../Keywords';
import SeoSection from '../SeoSection';
import { stripHtmlToPlainText } from '../SeoSection/helpers/stripHtmlToPlainText';
import Tooltip from '../../Tooltip/Tooltip';
import ProductMetaFields from '../ProductMetafields';
import { useBindProductField } from '../hooks';
import {
  resolveStagingListingMeta,
  resolveStagingVariantMeta,
} from './helpers/resolveStagingMeta';
import styles from '../styles.module.scss';

const l10n = localization.PRODUCT_INFO;
const masterPreviewTooltip = l10n.variantNotOnChannelPreviewTooltip;

const StagingTab = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { productId, variantId } = useMemo(() => getActiveProduct(location), [location]);
  const product = useSelector((state) => selectSelectedListingProduct(state, productId));
  const masterProduct = useSelector((state) => selectMasterProduct(state, productId));
  const listingVariants = useSelector((state) => selectSelectedListingVariants(state, productId));
  const stagingDiff = useSelector((state) => selectStagingDiff(state, productId));
  const variantStagingDiff = useSelector((state) => selectVariantStagingDiff(state, productId, variantId));
  const isBusy = useSelector((state) => selectProductIsBusy(state, productId));
  const { contextChannelId } = useSelector((state) => selectEffectiveChannelState(state, productId, variantId));
  const listingVariant = variantId
    ? listingVariants.find(({ masterVariantId }) => masterVariantId === variantId) ?? null
    : null;
  const masterVariant = useMemo(
    () => (variantId ? masterProduct.variants?.find(({ _id }) => _id === variantId) ?? null : null),
    [masterProduct.variants, variantId],
  );
  const variant = variantId ? (listingVariant ?? masterVariant) : null;
  const isMasterPreview = Boolean(variantId && !listingVariant && masterVariant);
  const showParentCommerceFields = listingVariants.length === 0;
  const effectiveMeta = useMemo(
    () => resolveStagingListingMeta(product?.meta),
    [product?.meta],
  );
  const effectiveVariantMeta = useMemo(
    () => resolveStagingVariantMeta({
      variantId,
      isMasterPreview,
      masterVariantMeta: masterVariant?.meta,
      listingVariantMeta: listingVariant?.meta,
    }),
    [variantId, isMasterPreview, masterVariant?.meta, listingVariant?.meta],
  );

  const [panelsVisibility, setPanelsVisibility] = useState(getDetailsPanelVisibility());

  const toggleVisibility = useCallback((title) => {
    const detailsPanelVisibility = { ...panelsVisibility };
    detailsPanelVisibility[title] = !detailsPanelVisibility[title];
    setCookie('picsio.detailsPanelVisibility', JSON.stringify(detailsPanelVisibility));
    setPanelsVisibility(detailsPanelVisibility);
  }, [panelsVisibility]);

  const onUpdateStagingProduct = useCallback((field, value) => {
    if (isBusy) return;
    dispatch(updateListing({
      listingId: product._id,
      masterProductId: productId,
      channelId: product.salesChannel,
      data: { [field]: value },
    }));
  }, [dispatch, isBusy, product._id, product.salesChannel, productId]);

  const bindProductField = useBindProductField(onUpdateStagingProduct);

  const onAddKeyword = useCallback((keywordName) => dispatch(addListingKeywords({
    productId: product._id, masterProductId: productId, channelId: product.salesChannel, keywordName,
  })), [dispatch, product._id, product.salesChannel, productId]);

  const onRemoveKeyword = useCallback((keywordIds) => dispatch(removeListingKeywords({
    productId: product._id, masterProductId: productId, channelId: product.salesChannel, keywordIds,
  })), [dispatch, product._id, product.salesChannel, productId]);

  const isCoreDiff = stagingDiff.title || stagingDiff.status
    || stagingDiff.vendor || stagingDiff.productType || stagingDiff.descriptionHtml || stagingDiff.keywords
    || stagingDiff.category;
  const isSeoDiff = stagingDiff.handle || stagingDiff.seoTitle || stagingDiff.seoDescription;
  const isPricingDiff = variantStagingDiff.price || variantStagingDiff.compareAtPrice || variantStagingDiff.taxable || variantStagingDiff.unitCost;
  const isInventoryDiff = variantStagingDiff.sku || variantStagingDiff.barcode;
  const isShippingDiff = variantStagingDiff.weight || variantStagingDiff.requiresShipping
    || variantStagingDiff.countryCodeOfOrigin || variantStagingDiff.harmonizedSystemCode;
  const isSimplePricingDiff = stagingDiff.price || stagingDiff.compareAtPrice
    || stagingDiff.taxable || stagingDiff.unitCost;
  const isSimpleSkuCodesDiff = stagingDiff.sku || stagingDiff.barcode;
  const isSimpleShippingDiff = stagingDiff.weight || stagingDiff.requiresShipping
    || stagingDiff.countryCodeOfOrigin || stagingDiff.harmonizedSystemCode;

  const updateListingVariantField = useCallback((listingVariantId, data) => {
    if (isBusy || !listingVariantId) return;
    dispatch(updateListingVariant({
      listingProductId: product._id,
      listingVariantId,
      masterProductId: productId,
      channelId: product.salesChannel,
      data,
    }));
  }, [dispatch, isBusy, product._id, product.salesChannel, productId]);

  const onChangePrice = useCallback((price) => {
    updateListingVariantField(listingVariant?._id, { price });
  }, [updateListingVariantField, listingVariant]);

  const onChangeCompareAtPrice = useCallback((compareAtPrice) => {
    updateListingVariantField(listingVariant?._id, { compareAtPrice });
  }, [updateListingVariantField, listingVariant]);

  const onChangeTaxable = useCallback((taxable) => {
    updateListingVariantField(listingVariant?._id, { taxable });
  }, [updateListingVariantField, listingVariant]);

  const onChangeUnitCost = useCallback((unitCost) => {
    updateListingVariantField(listingVariant?._id, { unitCost });
  }, [updateListingVariantField, listingVariant]);

  const onChangeSku = useCallback((sku) => {
    updateListingVariantField(listingVariant?._id, { sku });
  }, [updateListingVariantField, listingVariant]);

  const onChangeBarcode = useCallback((barcode) => {
    updateListingVariantField(listingVariant?._id, { barcode });
  }, [updateListingVariantField, listingVariant]);

  const onChangeTracked = useCallback((tracked) => {
    updateListingVariantField(listingVariant?._id, { tracked });
  }, [updateListingVariantField, listingVariant]);

  // null means the user cleared the weight — forward it as the clear instead of destructuring it.
  const onChangeWeight = useCallback((weight) => {
    updateListingVariantField(listingVariant?._id, {
      weight: weight ? { value: weight.value, unit: weight.unit } : null,
    });
  }, [updateListingVariantField, listingVariant]);

  const onChangeRequiresShipping = useCallback((requiresShipping) => {
    updateListingVariantField(listingVariant?._id, { requiresShipping });
  }, [updateListingVariantField, listingVariant]);

  const onChangeCountryCodeOfOrigin = useCallback((countryCodeOfOrigin) => {
    updateListingVariantField(listingVariant?._id, { countryCodeOfOrigin });
  }, [updateListingVariantField, listingVariant]);

  const onChangeHarmonizedSystemCode = useCallback((harmonizedSystemCode) => {
    updateListingVariantField(listingVariant?._id, { harmonizedSystemCode });
  }, [updateListingVariantField, listingVariant]);

  const onChangeListingCost = useCallback((cost) => {
    onUpdateStagingProduct('cost', cost);
  }, [onUpdateStagingProduct]);

  const onNavigateToListingParent = useCallback(() => {
    navigateToVariantParent({ productId });
  }, [productId]);

  return (
    <>
      <If condition={!variant}>
        <div className={styles.statusSelect}>
          <ProductStatusSelect
            productStatus={product.status}
            onChange={bindProductField('status')}
          />
        </div>
      </If>
      <If condition={!variant}>
        <DetailsBlock
          dataQa="productCore"
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productCoreVisibility"
          titleActions={[
            <RelativeTime key="lastUpdated" value={product?.updatedAt}>
              {(time) => (
                <span className={styles.lastPublished}>
                  {localization.PRODUCT_INFO.lastUpdated(time)}
                </span>
              )}
            </RelativeTime>,
          ]}
          blockTitle={<Tooltip content={isCoreDiff ? 'Unpublished changes' : null} placement="top"><span className={isCoreDiff ? styles.diffDot : undefined}>Core</span></Tooltip>}
        >
          <ProductName value={product?.title || ''} isDiff={stagingDiff.title} onChange={bindProductField('title')} />
          <Brand value={product?.vendor || ''} isDiff={stagingDiff.vendor} onChange={bindProductField('vendor')} />
          <ProductType
            value={product?.productType || ''}
            isDiff={stagingDiff.productType}
            onChange={bindProductField('productType')}
          />
          <If condition={picsioConfig.isPim}>
            <Category
              value={product?.category || null}
              isDiff={stagingDiff.category}
              onChange={bindProductField('category')}
              disabled={isBusy}
            />
          </If>
          <Description
            title={<Tooltip content={stagingDiff?.descriptionHtml ? 'Unpublished changes' : null} placement="top"><span className={stagingDiff?.descriptionHtml ? styles.diffDot : undefined}>Description</span></Tooltip>}
            currentValue={product?.descriptionHtml || ''}
            onChange={bindProductField('descriptionHtml')}
          />
          <br />
          <InputLabel position="top" margin>
            <Tooltip content={stagingDiff.keywords ? 'Unpublished changes' : null} placement="top">
              <span className={stagingDiff.keywords ? styles.diffDot : undefined}>Keywords</span>
            </Tooltip>
          </InputLabel>
          <Keywords
            productId={productId}
            keywords={product?.keywords || []}
            onAddKeyword={onAddKeyword}
            onRemoveKeyword={onRemoveKeyword}
          />
        </DetailsBlock>
        <br />
        <SeoSection
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productSeoVisibility"
          blockTitle={(
            <Tooltip content={isSeoDiff ? 'Unpublished changes' : null} placement="top">
              <span className={isSeoDiff ? styles.diffDot : undefined}>{l10n.seo.sectionTitle}</span>
            </Tooltip>
          )}
          handle={product?.handle || ''}
          seoTitle={product?.seoTitle || ''}
          seoDescription={product?.seoDescription || ''}
          fallbackTitle={product?.title || ''}
          fallbackDescription={stripHtmlToPlainText(product?.descriptionHtml || product?.description || '')}
          diff={{
            handle: stagingDiff.handle,
            seoTitle: stagingDiff.seoTitle,
            seoDescription: stagingDiff.seoDescription,
          }}
          onChangeHandle={bindProductField('handle')}
          onChangeSeoTitle={bindProductField('seoTitle')}
          onChangeSeoDescription={bindProductField('seoDescription')}
        />
        <br />
        <Options
          readOnly
          showDisabledManagementActions
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productOptionsVisibility"
          blockTitle={l10n.variantStructure}
          currentValue={product?.availableOptions || []}
        />
        <If condition={showParentCommerceFields}>
          <br />
          <Pricing
            price={product?.price ?? ''}
            compareAtPrice={product?.compareAtPrice ?? ''}
            taxable={product?.taxable || false}
            unitCost={getVariantUnitCost(product)}
            diff={{
              price: stagingDiff.price,
              compareAtPrice: stagingDiff.compareAtPrice,
              taxable: stagingDiff.taxable,
              unitCost: stagingDiff.unitCost,
            }}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productPricingVisibility"
            blockTitle={<Tooltip content={isSimplePricingDiff ? 'Unpublished changes' : null} placement="top"><span className={isSimplePricingDiff ? styles.diffDot : undefined}>Pricing</span></Tooltip>}
            onChangePrice={bindProductField('price')}
            onChangeCompareAtPrice={bindProductField('compareAtPrice')}
            onChangeTaxable={bindProductField('taxable')}
            onChangeUnitCost={onChangeListingCost}
          />
          <br />
          <Inventory
            {...INVENTORY_PRESETS.skuCodes}
            sku={product?.sku || ''}
            barcode={product?.barcode || ''}
            diff={{
              sku: stagingDiff.sku,
              barcode: stagingDiff.barcode,
            }}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productInventoryVisibility"
            blockTitle={(
              <Tooltip content={isSimpleSkuCodesDiff ? 'Unpublished changes' : null} placement="top">
                <span className={isSimpleSkuCodesDiff ? styles.diffDot : undefined}>{l10n.skuCodesBlockTitle}</span>
              </Tooltip>
            )}
            onChangeSku={bindProductField('sku')}
            onChangeBarcode={bindProductField('barcode')}
            onChangeTracked={() => null}
          />
          <br />
          <Shipping
            {...SHIPPING_PRESETS.simple}
            currentValue={product?.weight || { value: 0, unit: 'grams' }}
            requiresShipping={product?.requiresShipping || false}
            countryCodeOfOrigin={product?.countryCodeOfOrigin || ''}
            harmonizedSystemCode={product?.harmonizedSystemCode || ''}
            diff={{
              weight: stagingDiff.weight,
              requiresShipping: stagingDiff.requiresShipping,
              countryCodeOfOrigin: stagingDiff.countryCodeOfOrigin,
              harmonizedSystemCode: stagingDiff.harmonizedSystemCode,
            }}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productShippingVisibility"
            blockTitle={(
              <Tooltip content={isSimpleShippingDiff ? 'Unpublished changes' : null} placement="top">
                <span className={isSimpleShippingDiff ? styles.diffDot : undefined}>{l10n.shippingCompliance}</span>
              </Tooltip>
            )}
            onChangeRequiresShipping={bindProductField('requiresShipping')}
            onChangeWeight={bindProductField('weight')}
            onChangeCountryCodeOfOrigin={bindProductField('countryCodeOfOrigin')}
            onChangeHarmonizedSystemCode={bindProductField('harmonizedSystemCode')}
          />
        </If>
        <br />
        <If condition={picsioConfig.isPim}>
          <ProductMetaFields
            productId={productId}
            meta={effectiveMeta}
            metaDiff={stagingDiff.meta || {}}
            layer="listing"
            listingId={product._id}
            channelId={contextChannelId}
            attributeScope="product"
            isBusy={isBusy}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="productMetaVisibility"
          />
          <br />
        </If>
      </If>
      <If condition={variant}>
        <VariantDetailsHeader
          productTitle={product?.title || ''}
          variantDisplayName={variant?.displayName || variant?.title || ''}
          availableOptions={product?.availableOptions || []}
          variantOptions={variant?.options || {}}
        />
        <VariantContextBlock
          parentTitle={product?.title || ''}
          parentSku={product?.sku || ''}
          parentStatus={product?.status}
          availableOptions={product?.availableOptions || []}
          variantOptions={variant?.options || {}}
          onNavigateToParent={onNavigateToListingParent}
        />
        <br />
        <Pricing
          readOnly={isMasterPreview}
          readOnlyTooltip={isMasterPreview ? masterPreviewTooltip : undefined}
          price={variant?.price ?? ''}
          compareAtPrice={variant?.compareAtPrice ?? ''}
          taxable={variant?.taxable || false}
          unitCost={getVariantUnitCost(variant)}
          diff={variantStagingDiff}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productPricingVisibility"
          blockTitle={<Tooltip content={isPricingDiff ? 'Unpublished changes' : null} placement="top"><span className={isPricingDiff ? styles.diffDot : undefined}>Pricing</span></Tooltip>}
          onChangePrice={onChangePrice}
          onChangeCompareAtPrice={onChangeCompareAtPrice}
          onChangeTaxable={onChangeTaxable}
          onChangeUnitCost={onChangeUnitCost}
        />
        <br />
        <Inventory
          readOnly={isMasterPreview}
          readOnlyTooltip={isMasterPreview ? masterPreviewTooltip : undefined}
          {...INVENTORY_PRESETS.skuCodes}
          tracked={variant?.tracked || false}
          sku={variant?.sku || ''}
          barcode={variant?.barcode || ''}
          skuLabel={l10n.variantSku}
          diff={variantStagingDiff}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productInventoryVisibility"
          blockTitle={<Tooltip content={isInventoryDiff ? 'Unpublished changes' : null} placement="top"><span className={isInventoryDiff ? styles.diffDot : undefined}>{l10n.skuCodesBlockTitle}</span></Tooltip>}
          onChangeSku={onChangeSku}
          onChangeBarcode={onChangeBarcode}
          onChangeTracked={onChangeTracked}
        />
        <br />
        <Shipping
          readOnly={isMasterPreview}
          readOnlyTooltip={isMasterPreview ? masterPreviewTooltip : undefined}
          {...SHIPPING_PRESETS.variant}
          currentValue={variant?.weight || { value: 0, unit: 'grams' }}
          requiresShipping={variant?.requiresShipping || false}
          countryCodeOfOrigin={variant?.countryCodeOfOrigin || ''}
          harmonizedSystemCode={variant?.harmonizedSystemCode || ''}
          diff={variantStagingDiff}
          detailsPanelVisibility={panelsVisibility}
          toggleVisibility={toggleVisibility}
          blockName="productShippingVisibility"
          blockTitle={<Tooltip content={isShippingDiff ? 'Unpublished changes' : null} placement="top"><span className={isShippingDiff ? styles.diffDot : undefined}>Shipping</span></Tooltip>}
          onChangeRequiresShipping={onChangeRequiresShipping}
          onChangeWeight={onChangeWeight}
          onChangeCountryCodeOfOrigin={onChangeCountryCodeOfOrigin}
          onChangeHarmonizedSystemCode={onChangeHarmonizedSystemCode}
        />
        <br />
        <If condition={picsioConfig.isPim}>
          <ProductMetaFields
            productId={productId}
            variantId={variantId}
            listingVariantId={listingVariant?._id}
            meta={effectiveVariantMeta}
            metaDiff={variantStagingDiff.meta || {}}
            layer="listing"
            listingId={product._id}
            channelId={contextChannelId}
            attributeScope="variant"
            isBusy={isBusy}
            readOnly={isMasterPreview}
            readOnlyTooltip={isMasterPreview ? masterPreviewTooltip : undefined}
            detailsPanelVisibility={panelsVisibility}
            toggleVisibility={toggleVisibility}
            blockName="variantMetaVisibility"
          />
          <br />
        </If>
      </If>
    </>
  );
};

export default StagingTab;
